/**
 * TTS Service - Kokoro WebGPU
 * High-quality text-to-speech with auto language detection
 */
import { KokoroTTS } from 'kokoro-js';

class TTSService {
    constructor() {
        this.tts = null;
        this.isLoading = false;
        this.isReady = false;
        this.isSpeaking = false;
        this.currentAudio = null;
        this.defaultVoice = 'af_heart';
    }

    /**
     * Voice mappings by language
     * Note: ONNX version only has EN voices, using af_bella for French (good pronunciation)
     */
    static VOICES = {
        'en': 'af_heart',      // American English
        'en-US': 'af_heart',
        'en-GB': 'bf_emma',    // British English
        'fr': 'af_bella',      // French (no native FR voice in ONNX)
        'fr-FR': 'af_bella',
        'default': 'af_heart'
    };

    /**
     * Initialize the TTS engine
     */
    async init(onProgress = () => { }) {
        if (this.isLoading) return false;
        if (this.isReady) return true;

        this.isLoading = true;

        try {
            onProgress({ text: 'Chargement TTS Kokoro...', progress: 10 });

            // Use ONNX model for browser compatibility
            this.tts = await KokoroTTS.from_pretrained('onnx-community/Kokoro-82M-v1.0-ONNX', {
                dtype: 'fp32',
                device: 'webgpu',
            });

            this.isReady = true;
            this.isLoading = false;
            onProgress({ text: 'TTS Kokoro prêt !', progress: 100 });
            return true;
        } catch (error) {
            console.error('WebGPU TTS failed, trying WASM:', error);

            try {
                onProgress({ text: 'Fallback WASM...', progress: 50 });

                this.tts = await KokoroTTS.from_pretrained('onnx-community/Kokoro-82M-v1.0-ONNX', {
                    dtype: 'q8',
                    device: 'wasm',
                });

                this.isReady = true;
                this.isLoading = false;
                onProgress({ text: 'TTS prêt (WASM)', progress: 100 });
                return true;
            } catch (fallbackError) {
                this.isLoading = false;
                throw fallbackError;
            }
        }
    }

    /**
     * Detect language from text
     * Improved heuristic with ratio-based scoring
     */
    detectLanguage(text) {
        const lowerText = text.toLowerCase();

        // French accent characters (strong indicator)
        const frenchAccents = (text.match(/[àâäéèêëïîôùûüÿœæç]/gi) || []).length;

        // Common French words (very common in any French text)
        const frenchWords = [
            'je', 'tu', 'il', 'elle', 'on', 'nous', 'vous', 'ils', 'elles',
            'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'au', 'aux',
            'et', 'ou', 'mais', 'donc', 'car', 'ni', 'que', 'qui', 'quoi',
            'ce', 'cette', 'ces', 'cet', 'mon', 'ton', 'son', 'ma', 'ta', 'sa',
            'est', 'sont', 'suis', 'es', 'sommes', 'êtes', 'être', 'avoir', 'fait',
            'pour', 'avec', 'dans', 'sur', 'sous', 'par', 'chez', 'vers',
            'pas', 'plus', 'moins', 'très', 'bien', 'aussi', 'encore',
            'oui', 'non', 'peut', 'tout', 'tous', 'toute', 'toutes',
            'comme', 'quand', 'comment', 'pourquoi', 'où', 'parce',
            'voici', 'voilà', 'merci', 'bonjour', 'salut', 'alors',
            'si', 'ne', "n'", "l'", "d'", "j'", "c'", "s'", "qu'",
            'ai', 'as', 'avons', 'avez', 'ont', 'été', 'était', 'sera'
        ];

        // Count French word matches
        let frenchWordCount = 0;
        const words = lowerText.split(/\s+/);
        for (const word of words) {
            const cleanWord = word.replace(/[.,!?;:'"()-]/g, '');
            if (frenchWords.includes(cleanWord)) {
                frenchWordCount++;
            }
        }

        // Calculate French score
        const frenchScore = frenchAccents * 2 + frenchWordCount;
        const wordCount = words.length;
        const frenchRatio = frenchScore / Math.max(wordCount, 1);

        // Debug log
        console.log(`Language detection: ${wordCount} words, ${frenchAccents} accents, ${frenchWordCount} FR words, ratio: ${frenchRatio.toFixed(2)}`);

        // If ratio > 0.15 or score > 2, it's likely French
        if (frenchRatio > 0.15 || frenchScore > 2) {
            return 'fr';
        }

        // Default to English
        return 'en';
    }

    /**
     * Get voice for detected language
     */
    getVoiceForLanguage(lang) {
        return TTSService.VOICES[lang] || TTSService.VOICES['en'] || this.defaultVoice;
    }

    /**
     * Speak text with language selection
     * @param {string} text - Text to speak
     * @param {string} lang - Language code (e.g., 'fr', 'en') - if null, auto-detect
     */
    async speak(text, lang = null) {
        if (!this.isReady || !this.tts) {
            throw new Error('TTS not initialized');
        }

        this.stop();
        this.isSpeaking = true;

        try {
            // Use provided language or detect from text
            const useLang = lang || this.detectLanguage(text);
            const voice = this.getVoiceForLanguage(useLang);

            console.log(`TTS: Language "${useLang}", using voice "${voice}"`);

            // Generate audio
            const audio = await this.tts.generate(text, { voice });

            // Play audio
            await this.playAudioData(audio);

            this.isSpeaking = false;
        } catch (error) {
            console.error('TTS error:', error);
            this.isSpeaking = false;
            throw error;
        }
    }

    /**
     * Play audio data in browser
     */
    async playAudioData(audioResult) {
        return new Promise((resolve, reject) => {
            try {
                const samples = audioResult.audio;
                const sampleRate = audioResult.sampling_rate || 24000;

                const wavBlob = this.float32ToWav(samples, sampleRate);
                const audioUrl = URL.createObjectURL(wavBlob);
                const audioElement = new Audio(audioUrl);

                this.currentAudio = audioElement;

                audioElement.onended = () => {
                    URL.revokeObjectURL(audioUrl);
                    this.currentAudio = null;
                    this.isSpeaking = false;
                    resolve();
                };

                audioElement.onerror = () => {
                    URL.revokeObjectURL(audioUrl);
                    this.currentAudio = null;
                    this.isSpeaking = false;
                    reject(new Error('Playback error'));
                };

                audioElement.play().catch(reject);
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Convert Float32Array to WAV blob
     */
    float32ToWav(samples, sampleRate) {
        const buffer = new ArrayBuffer(44 + samples.length * 2);
        const view = new DataView(buffer);

        const writeString = (offset, str) => {
            for (let i = 0; i < str.length; i++) {
                view.setUint8(offset + i, str.charCodeAt(i));
            }
        };

        writeString(0, 'RIFF');
        view.setUint32(4, 36 + samples.length * 2, true);
        writeString(8, 'WAVE');
        writeString(12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, 1, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * 2, true);
        view.setUint16(32, 2, true);
        view.setUint16(34, 16, true);
        writeString(36, 'data');
        view.setUint32(40, samples.length * 2, true);

        for (let i = 0; i < samples.length; i++) {
            const sample = Math.max(-1, Math.min(1, samples[i]));
            view.setInt16(44 + i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        }

        return new Blob([buffer], { type: 'audio/wav' });
    }

    /**
     * Stop playback
     */
    stop() {
        if (this.currentAudio) {
            this.currentAudio.pause();
            this.currentAudio.currentTime = 0;
            this.currentAudio = null;
        }
        this.isSpeaking = false;
    }

    getStatus() {
        return {
            isLoading: this.isLoading,
            isReady: this.isReady,
            isSpeaking: this.isSpeaking
        };
    }
}

export const ttsService = new TTSService();
export default ttsService;
